import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserRanking } from '../types/rankings';
import { useAuthState } from '../hooks/useAuthState';
import { supabase, handleSupabaseError, handleNetworkError } from '../config/supabase';

interface RankingsContextType {
  allTimeRankings: UserRanking[];
  weeklyRankings: UserRanking[];
  loading: boolean;
}

const RankingsContext = createContext<RankingsContextType | undefined>(undefined);

export const RankingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [allTimeRankings, setAllTimeRankings] = useState<UserRanking[]>([]);
  const [weeklyRankings, setWeeklyRankings] = useState<UserRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthState();

  useEffect(() => {
    const loadRankings = async () => {
      if (!user) {
        setAllTimeRankings([]);
        setWeeklyRankings([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Fetch all-time rankings from user_stats
        const { data: allTimeData, error: allTimeError } = await supabase
          .from('user_stats')
          .select(`
            user_id,
            total_focus_time,
            completed_pomodoros
          `)
          .order('total_focus_time', { ascending: false })
          .limit(10);

        if (allTimeError) throw allTimeError;

        // Fetch weekly rankings from weekly_stats
        const { data: weeklyData, error: weeklyError } = await supabase
          .from('weekly_stats')
          .select(`
            user_id,
            focus_time,
            completed_pomodoros
          `)
          .order('focus_time', { ascending: false })
          .limit(10);

        if (weeklyError) throw weeklyError;

        // Get all user IDs from both rankings
        const allUserIds = [
          ...(allTimeData?.map(item => item.user_id) || []),
          ...(weeklyData?.map(item => item.user_id) || [])
        ];

        // Fetch user data for all users
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, email, avatar_url')
          .in('id', allUserIds);

        if (usersError) throw usersError;

        const allTimeRankings = allTimeData?.map((item, index) => {
          const user = usersData?.find(u => u.id === item.user_id);
          return {
            id: item.user_id,
            name: user?.name || 'Unknown User',
            email: user?.email || '',
            avatarUrl: user?.avatar_url || '',
            totalMinutes: item.total_focus_time,
            totalPomodoros: item.completed_pomodoros,
            rank: index + 1,
          };
        }) || [];

        const weeklyRankings = weeklyData?.map((item, index) => {
          const user = usersData?.find(u => u.id === item.user_id);
          return {
            id: item.user_id,
            name: user?.name || 'Unknown User',
            email: user?.email || '',
            avatarUrl: user?.avatar_url || '',
            totalMinutes: item.focus_time,
            totalPomodoros: item.completed_pomodoros,
            rank: index + 1,
          };
        }) || [];

        setAllTimeRankings(allTimeRankings);
        setWeeklyRankings(weeklyRankings);
      } catch (error) {
        console.error('Error loading rankings:', error);
        if (handleNetworkError(error)) {
          console.warn('Network error detected, using empty rankings');
        } else {
          handleSupabaseError(error);
        }
        setAllTimeRankings([]);
        setWeeklyRankings([]);
      } finally {
        setLoading(false);
      }
    };

    // Rankings disabled — community feature is off
    // loadRankings();
  }, [user]);

  return (
    <RankingsContext.Provider value={{
      allTimeRankings,
      weeklyRankings,
      loading
    }}>
      {children}
    </RankingsContext.Provider>
  );
};

export const useRankings = () => {
  const context = useContext(RankingsContext);
  if (context === undefined) {
    throw new Error('useRankings must be used within a RankingsProvider');
  }
  return context;
};